import { ENVIRONMENT_PRESETS, type EnvironmentPreset, type EnvironmentSettings } from '@v2/contracts'

const STORAGE_KEY = 'tabletop.v2.environment'
type Preferences = { preset: EnvironmentPreset; motion: boolean }
type PreferenceStore = Pick<Storage, 'getItem' | 'setItem'>

function validPreset(value: unknown): value is EnvironmentPreset {
  return ENVIRONMENT_PRESETS.some(preset => preset.id === value)
}

/** Local presentation preferences and a clock that never affect game state. */
export class EnvironmentPresentation {
  private override: Preferences | null = null
  private defaults: Preferences = { preset: 'ocean', motion: true }
  private elapsed = 0

  constructor(private readonly storage?: PreferenceStore) {
    try {
      const stored: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? 'null')
      if (stored && typeof stored === 'object' && 'preset' in stored && validPreset(stored.preset)) {
        this.override = { preset: stored.preset, motion: !('motion' in stored) || stored.motion !== false }
      }
    } catch { /* Unavailable or damaged preferences do not prevent play. */ }
  }

  get settings(): Preferences { return this.override ?? this.defaults }
  get time(): number { return this.elapsed }

  configure(settings?: EnvironmentSettings): void {
    this.defaults = { preset: settings && validPreset(settings.preset) ? settings.preset : 'ocean', motion: settings?.motion !== false }
  }

  setPreset(preset: EnvironmentPreset): void {
    if (!validPreset(preset)) return
    this.save({ ...this.settings, preset })
  }

  setMotion(motion: boolean): void { this.save({ ...this.settings, motion }) }

  advance(seconds: number, reducedMotion: boolean): void {
    if (this.settings.motion && !reducedMotion && Number.isFinite(seconds) && seconds > 0) this.elapsed += seconds
  }

  private save(preferences: Preferences): void {
    this.override = preferences
    try { this.storage?.setItem(STORAGE_KEY, JSON.stringify(preferences)) } catch { /* Private browsing still supports session-local preferences. */ }
  }
}

export function environmentStorage(): PreferenceStore | undefined {
  try { return globalThis.localStorage } catch { return undefined }
}
